import { Box as BaseBox, Container, Flex, Image, Text } from '@chakra-ui/react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import styled from 'styled-components'

import { Icon } from '../../components/Icon'
import { formatPhone } from '../../helpers'
import { VideoQuizService } from '../../services/VideoQuiz/VideoQuiz.service'
import { magenta } from '../../theme'

const Button = styled.button`
  background-color: ${magenta[500]};
  color: #fefefe;
  box-shadow: 0 0 6px rgba(0, 0, 0, 0.2);
  border-radius: 5px;
  font-size: 16px;
  padding: 8px 16px;
  margin-top: 16px;
`

const CustomLabel = styled.label`
  border: 0;
  font-size: 22px;
  margin: 0;
  padding: 0;
  display: block;
  color: #fefefe;
`

const CustomInput = styled.input`
  border: none;
  border-bottom: 1px solid ${magenta[500]};
  background-color: transparent;
  color: ${magenta[500]};
  font-size: 22px;
  padding: 8px 0;
  width: 100%;
  outline: none;
  margin-bottom: 1px;

  &:focus {
    border-bottom: 2px solid ${magenta[500]};
    margin-bottom: 0px;
  }
`

const Box = styled(BaseBox)`
  width: 100%;
`

export const VideoQuizForm = () => {
  const nav = useNavigate()
  const videoQuizService = new VideoQuizService()
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: ''
  })
  const [saving, setSaving] = useState(false)
  const [errors, setErrors] = useState<{ name: string[]; email: string[]; phone: string[] }>({
    name: [],
    email: [],
    phone: []
  })
  const [step, setStep] = useState(1)

  const handlePhoneUpdate = (event: React.ChangeEvent<HTMLInputElement>) => {
    setForm({ ...form, phone: formatPhone(event.target.value) })
  }

  const nextStep = () => {
    setErrors({ name: [], email: [], phone: [] })

    if (step === 2) {
      const nameRegex = /^[a-zA-Z]+(?:\s[a-zA-Z]+)+$/
      if (!nameRegex.test(form.name)) {
        setErrors({ ...errors, name: ['Nome inválido. Por favor, insira seu nome completo.'] })
        return
      }
    }

    if (step === 3) {
      const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/
      if (!emailRegex.test(form.email)) {
        setErrors({ ...errors, email: ['E-mail inválido. Por favor, insira um e-mail válido.'] })
        return
      }
    }

    if (step === 4) {
      const phoneRegex = /^\([1-9]{2}\) [0-9]{4,5}-[0-9]{4}$/
      if (!phoneRegex.test(form.phone)) {
        setErrors({ ...errors, phone: ['Telefone inválido. Por favor, insira um telefone válido.'] })
        return
      }
    }

    setStep(step === 5 ? 5 : step + 1)
  }

  const prevStep = () => {
    setStep(step === 1 ? 1 : step - 1)
  }

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault()

    if (saving) {
      return
    }

    setSaving(true)

    videoQuizService
      .store(form)
      .then((response) => {
        nav(`/video-quiz/${response.uuid}`)
      })
      .catch((error) => {
        if (error.response?.status !== 422) {
          return
        }

        setErrors(error.response.data.errors)
      })
      .finally(() => {
        setSaving(false)
      })
  }

  return (
    <>
      <Image src="/img/logo.png" alt="ABRA" w="35%" maxW="140px" ml="3" mt="3" position={'absolute'} />
      <form onSubmit={handleSubmit}>
        <Container backgroundImage="url('/img/magenta-background.png')" backgroundSize="cover">
          <Flex>
            <Flex flex="1" minH="100vh" align="center">
              <Box display={step === 1 ? 'block' : 'none'}>
                <Text fontSize="2xl" color="#fefefe" mb="3">
                  Preencha o formulário para participar do quiz
                </Text>
                <Text fontSize="sm" color="#fefefe" mb="3">
                  Lorem ipsum dolor sit amet, consectetur adipiscing elit.
                </Text>
                <Text fontSize="sm" color="#fefefe" mb="3">
                  Proin dolor ipsum, blandit sed nulla vel, eleifend luctus magna.
                </Text>
                <Text fontSize="sm" color="#fefefe" mb="3">
                  Quisque a erat hendrerit mi tempus lobortis eget vitae elit. Nam efficitur arcu ac velit aliquet
                  maximus.{' '}
                </Text>
                <Button type="button" onClick={nextStep} display="flex" alignItems="center">
                  Iniciar <Icon name="lni lni-arrow-right" ml="7px" />
                </Button>
              </Box>
              <Box display={step === 2 ? 'block' : 'none'}>
                <CustomLabel>Qual seu nome?</CustomLabel>
                <CustomInput
                  placeholder="Preencha seu nome completo"
                  value={form.name}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, name: e.target.value })}
                  required
                />
                <Box color="red" fontSize="sm">
                  {errors.name.map((error) => (
                    <div key={error}>{error}</div>
                  ))}
                </Box>
                <Button type="button" onClick={nextStep}>
                  Próximo
                </Button>
              </Box>
              <Box display={step === 3 ? 'block' : 'none'}>
                <CustomLabel>Ok {form.name.split(' ')[0]}, agora informe o seu e-mail</CustomLabel>
                <CustomInput
                  placeholder="exemplo@exemplo.com"
                  type="email"
                  value={form.email}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setForm({ ...form, email: e.target.value })}
                  required
                />
                <Box color="red" fontSize="sm">
                  {errors.email.map((error) => (
                    <div key={error}>{error}</div>
                  ))}
                </Box>
                <Button type="button" onClick={nextStep}>
                  Próximo
                </Button>
              </Box>
              <Box display={step === 4 ? 'block' : 'none'}>
                <CustomLabel>Agora informe um telefone com WhatsApp</CustomLabel>
                <CustomInput
                  placeholder="(00) 00000-0000"
                  inputMode="tel"
                  maxLength={20}
                  value={form.phone}
                  onChange={handlePhoneUpdate}
                  required
                />
                <Box color="red" fontSize="sm">
                  {errors.phone.map((error) => (
                    <div key={error}>{error}</div>
                  ))}
                </Box>
                <Button type="button" onClick={nextStep}>
                  Próximo
                </Button>
              </Box>
              <Box display={step === 5 ? 'block' : 'none'}>
                <Text fontSize="2xl" color="#fefefe" mb="3">
                  Tudo pronto! Clique em &quot;Iniciar&quot; para começar o quiz!
                </Text>
                <Button type="submit" onClick={nextStep}>
                  {saving ? 'Iniciando...' : 'Iniciar'}
                </Button>
              </Box>
            </Flex>
            <Flex flex="0 0 2rem" minH="100vh" align="center">
              <Flex flexDirection="column" alignItems="center" w="2rem" justifyItems={'center'}>
                <Icon
                  name="lni lni-arrow-up"
                  size="lg"
                  color="white"
                  mb="20px"
                  fontWeight="bolder"
                  opacity={step === 1 ? 0.2 : 1}
                  onClick={prevStep}
                />
                <Icon
                  name="lni lni-arrow-down"
                  size="lg"
                  color="white"
                  fontWeight="bolder"
                  opacity={step === 5 ? 0.2 : 1}
                  onClick={nextStep}
                />
              </Flex>
            </Flex>
          </Flex>
        </Container>
      </form>
    </>
  )
}
